import { apiRequest } from './httpRequest';
import type { ChemIsomerV1Document } from '@peruselab/structure-editor/domain/isomerDocument';

export interface IsomerSummary {
  readonly id: string;
  readonly moleculeId: string;
  readonly documentRevision: number;
  readonly createdAt: string;
}

export interface IsomerResponse {
  readonly id: string;
  readonly documentRevision: number;
  readonly schemaVersion: string;
  readonly moleculeId: string;
  readonly document: ChemIsomerV1Document;
  readonly ownerType: string;
  readonly ownerId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// #127: given a Molecule-level search match, the isomer picker needs this to find the specific
// Isomer(s) a Reaction participant can actually reference -- search only ever returns moleculeId.
export async function listIsomersByMolecule(moleculeId: string): Promise<readonly IsomerSummary[]> {
  return apiRequest<IsomerSummary[]>(`/isomers?${new URLSearchParams({ moleculeId })}`, 'isomer');
}

// #147: creating an Isomer directly, needed by the client save/load rewrite. Isomer-level dedup
// stays deliberately out of scope (#141 decision 2) -- always mints a new row, even if content-
// identical to one already under the same Molecule.
export async function createIsomer(document: ChemIsomerV1Document): Promise<IsomerResponse> {
  return apiRequest<IsomerResponse>('/isomers', 'isomer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

// #1066 step 3 (ADR 0019 D5, D9): an Isomer just drawn -- the same stereoisomer if PeruseLab has it,
// else new -- registered for the caller's space, private and provisional until something saved uses it.
export async function registerDrawnIsomer(document: ChemIsomerV1Document): Promise<IsomerResponse> {
  return apiRequest<IsomerResponse>('/isomers/drawn', 'isomer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

// #128: fetching a picked Isomer's own stereochemistry (needed alongside its Molecule's graph to
// generate a fresh depiction -- an Isomer has no coordinates of its own).
export async function getIsomer(id: string): Promise<IsomerResponse> {
  return apiRequest<IsomerResponse>(`/isomers/${encodeURIComponent(id)}`, 'isomer');
}
